import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';
import { TenantIntegrationStatusType, TenantOauthStatusType, ZappaTenantStatus, TenantSyncStatusType } from 'src/app/core/interfaces/tenants';
import { AdminService } from 'src/app/core/services/admin.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { TenantsService } from 'src/app/core/services/tenants.service';
import { Util } from 'src/app/core/utils/Util';
import * as _ from 'lodash';

const logContext = 'ZappaAuxDialogComponent';

@Component({
  selector: 'app-zappa-aux-dialog',
  templateUrl: './zappa-aux-dialog.component.html',
  styleUrls: ['./zappa-aux-dialog.component.scss']
})
export class ZappaAuxDialogComponent {

  currentStatus: ZappaTenantStatus;
  TenantIntegrationStatusType = TenantIntegrationStatusType;
  TenantOauthStatusType = TenantOauthStatusType;
  TenantSyncStatusType = TenantSyncStatusType;
  
  showFormError: boolean = false;
  errMsg: string;
  errMsgStyle:  any = { 'max-width': '500px' };

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
      public dialogRef: MatDialogRef<ZappaAuxDialogComponent>,
      private tenantsService: TenantsService,
      private adminService: AdminService,
      private translateService: TranslateService,
      private spinnerService: SpinnerService
  ) {}

  ngOnInit() {
		this.spinnerService.setSpinnerMessageByKey('app.loading');
		this.spinnerService.showSpinner();
    return this.adminService.getAccountTagsNoFrills(this.data.account.accountId).then( async tags => {
      if ( tags.Zappa?.tenantId )
      {
        try
        {
          let status = await this.tenantsService.getTenantStatus(tags.Zappa?.tenantId);
          let updatedStatus = this.interpretStatusIfRequired(status);
          this.currentStatus = updatedStatus;
          this.spinnerService.hideSpinner();
          return;
        }
        catch (err)
        {
          console.error(`${logContext}.ngOnInit getTenantStatus failed`, err);
          this.errMsg = this.translateService.instant('integrations.zappa.aux-dialog.statusRequestError') + ": " + Util.extractMessageFromError(err);
          this.showFormError = true;
          this.spinnerService.hideSpinner();
          return;
        }
      }
      else
      {
        // this is not a likely scenario. There should be no reason not to have a tenant id 
        // and if the account does not have one, it would not be in this dialog
        console.error(`${logContext}.ngOnInit there is no tenant so we should not even be here`, tags);
        this.errMsg = this.translateService.instant('integrations.zappa.aux-dialog.noStatusError')
        this.showFormError = true;
        this.spinnerService.hideSpinner();
        return;
      }
    })
    .catch(err => {
      console.error(`${logContext}.ngOnInit getAccountTagsNoFrills failed`, err);
      this.errMsg = this.translateService.instant('integrations.zappa.aux-dialog.statusRequestError') + ": " + Util.extractMessageFromError(err);
      this.showFormError = true;
      this.spinnerService.hideSpinner();
      return;
    })
  }

  interpretStatusIfRequired(status: ZappaTenantStatus): ZappaTenantStatus
  {
    // if there is no sync time, and the integration is not connected, append a message to what might not be there
    let newStatus = _.cloneDeep(status);

    // we have seen a situation where the integration state is NOT_CONNECTED and there is no lastSuccessfulSync state
    // unfortunately the micro does not supply adequate information so the UI has to figure it out
    if ( newStatus && newStatus.integration && 
      newStatus.integration.state == TenantIntegrationStatusType.NOT_CONNECTED && 
      newStatus?.lastSuccessfulSync?.state == undefined &&
      newStatus?.oauth.state == TenantOauthStatusType.AUTHORIZED)
    {
      newStatus.lastSuccessfulSync = {state: this.translateService.instant('integrations.zappa.aux-dialog.lastSyncNotAvail')};

      // add some clarity here for the UI and the user
      if (newStatus.integration.details) // need to append
      {
        newStatus.integration.details = newStatus.integration.details + '<br>' + this.translateService.instant('integrations.zappa.aux-dialog.integrationStatusStr.notConnectedNoSync');
      }
      else
      {
        newStatus.integration.details = this.translateService.instant('integrations.zappa.aux-dialog.integrationStatusStr.notConnectedNoSync');
      }
    }
    else if (newStatus && newStatus?.lastSuccessfulSync?.state == undefined)
    {
      newStatus.lastSuccessfulSync = {state: this.translateService.instant('integrations.zappa.aux-dialog.lastSyncNotAvail')};
    }
    else
    {
      newStatus.lastSuccessfulSync.state = new Date(newStatus.lastSuccessfulSync.state).toLocaleString();
    }

    if (newStatus && newStatus.oauth && newStatus.oauth.details == undefined && newStatus.oauth.state != TenantOauthStatusType.AUTHORIZED)
    {
      // translate the state to a known string to give more guidance
      newStatus.oauth.details = this.translateService.instant('integrations.zappa.status-message.oauth.' + newStatus.oauth.state);
    }

    return newStatus;
  }

  onDone() {
    this.dialogRef.close();
  }

}
